import React, { useState } from 'react';
import { Download, Share2, MessageCircle, Mail, Facebook, Copy, Check } from 'lucide-react';

const FLYER_URL = "https://customer-assets-cm19k8pv.emergentagent.net/job_e8d2b5f5-0cb7-4419-b7f1-41d20d77a12b/artifacts/f8dy9jim_IMG-20260820-WA0166.jpg";
const SITE_URL = "https://talithakoumitug.com";

const copy = {
  fr: {
    title: "Partagez l'affiche avec une amie",
    subtitle: "Chaque partage compte. Aidez-nous à toucher plus de jeunes filles.",
    download: "Télécharger l'affiche",
    whatsapp: "WhatsApp",
    email: "Courriel",
    facebook: "Facebook",
    copyLink: "Copier le lien",
    copied: "Lien copié !",
    shareText: "Rejoignez la Conférence Internationale 2027 de TALITHA KOUMI TUG — du 12 au 14 mars à Ottawa. Inscrivez-vous :",
    emailSubject: "Conférence Internationale 2026 — Ottawa",
  },
  en: {
    title: "Share the flyer with a friend",
    subtitle: "Every share matters. Help us reach more young women.",
    download: "Download flyer",
    whatsapp: "WhatsApp",
    email: "Email",
    facebook: "Facebook",
    copyLink: "Copy link",
    copied: "Link copied!",
    shareText: "Join the TALITHA KOUMI TUG International Conference 2026 — March 12-14 in Ottawa. Register here:",
    emailSubject: "International Conference 2026 — Ottawa",
  },
};

export default function ShareFlyerWidget({ language = 'fr' }) {
  const t = copy[language] || copy.fr;
  const [copied, setCopied] = useState(false);

  const shareText = `${t.shareText} ${SITE_URL}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      // silent
    }
  };

  const handleDownload = () => {
    // Trigger download by opening in new tab (browser will show save dialog for images)
    const a = document.createElement('a');
    a.href = FLYER_URL;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.download = 'talitha-koumi-tug-2026-affiche.jpeg';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SITE_URL)}&quote=${encodeURIComponent(shareText)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(t.emailSubject)}&body=${encodeURIComponent(shareText)}`;

  return (
    <div
      data-testid="share-flyer-widget"
      className="relative overflow-hidden rounded-2xl border border-[#CD0000]/20 bg-gradient-to-br from-[#FDF2F8] via-white to-[#FFF6FA] p-6 md:p-7"
    >
      {/* Decorative accent */}
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#CD0000]/10 blur-3xl pointer-events-none" />

      <div className="relative flex flex-col md:flex-row gap-6">
        {/* Flyer preview */}
        <a
          href={FLYER_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="share-flyer-preview"
          className="shrink-0 w-full md:w-40 aspect-[4/5] rounded-xl overflow-hidden shadow-lg ring-1 ring-black/5 hover:ring-[#CD0000]/40 transition-all group"
        >
          <img
            src={FLYER_URL}
            alt="Official 2026 Conference Flyer"
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </a>

        {/* Actions */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 mb-1">
            <Share2 className="w-5 h-5 text-[#CD0000] shrink-0 mt-0.5" />
            <h3 className="text-lg md:text-xl font-extrabold text-[#1D1D1D] leading-snug">
              {t.title}
            </h3>
          </div>
          <p className="text-sm text-gray-500 mb-5 md:ml-7">{t.subtitle}</p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            <button
              type="button"
              onClick={handleDownload}
              data-testid="share-download-btn"
              className="flex items-center justify-center gap-2 bg-[#CD0000] hover:bg-[#A00000] text-white font-semibold text-sm py-2.5 px-3 rounded-xl transition-colors col-span-2 sm:col-span-3"
            >
              <Download className="w-4 h-4" />
              {t.download}
            </button>

            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-whatsapp-btn"
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb757] text-white font-semibold text-sm py-2.5 px-3 rounded-xl transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              {t.whatsapp}
            </a>

            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="share-facebook-btn"
              className="flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#1565d0] text-white font-semibold text-sm py-2.5 px-3 rounded-xl transition-colors"
            >
              <Facebook className="w-4 h-4" />
              {t.facebook}
            </a>

            <a
              href={emailUrl}
              data-testid="share-email-btn"
              className="flex items-center justify-center gap-2 bg-gray-800 hover:bg-black text-white font-semibold text-sm py-2.5 px-3 rounded-xl transition-colors"
            >
              <Mail className="w-4 h-4" />
              {t.email}
            </a>

            <button
              type="button"
              onClick={handleCopyLink}
              data-testid="share-copy-link-btn"
              className={`flex items-center justify-center gap-2 font-semibold text-sm py-2.5 px-3 rounded-xl transition-colors col-span-2 sm:col-span-3 ${
                copied
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'bg-white text-[#1D1D1D] border border-gray-200 hover:border-[#CD0000]/50'
              }`}
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? t.copied : t.copyLink}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
